import {
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  CharacterFlow,
  Screen,
  setIsOverlayLogin,
  useAppDispatch,
  useLoadCharacter,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import getCharacterLorebookProfileIndicator from "@next-platform/client-common-core/lorebook/getCharacterLorebookProfileIndicator";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  Image,
  Spinner,
  SpinnerSize,
  Text,
  TextBox,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import TagSuggestion from "@src/components/TagSuggestion/TagSuggestion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CapabilityShareCharacterModal, Col, LocalizedLink, Row } from "@/components";
import { CharacterTranslation } from "@/components/CharacterTranslation/CharacterTranslation";
import AvatarWithModal from "@/components/common/AvatarWithModal";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useRecombee } from "@/hooks/useRecombee";
import { ANALYTICS_VIEW_PERMISSION } from "@/pages/CreatorAnalytics/constants";
import useAuth from "@/services/useAuth";
import { AvatarPreset } from "@/types";
import { formatNumber, getAvatarUrl, getBusinessRouteScreen } from "@/utils";
import formatCharacterRatingScore from "@/utils/formatCharacterRatingScore";
import { getIsPartner } from "@/utils/getIsPartner";

import NotFoundPage from "../NotFound/NotFound";

// TODO(igor-nextday): [NX-7407] move user-facing label to Namespace.CreatorAnalytics i18n keys.
const ANALYTICS_BUTTON_LABEL = "Analytics";

const NOT_FOUND_STATUS = 404;

export default function ChatbotProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const applicationId = basicAppConfig.applicationId;
  const dispatch = useAppDispatch();
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { onlyUserId, isAuthenticated } = useUserInfo();
  const { blurNSFW, likedBots, onUserLikebot } = useUserSettings();
  const { isMobile, isDesktop } = useMedia();
  const { characterId } = useParams();
  const { sendUserInteractionAddBookmark, recombeeUserId } = useRecombee();
  const {
    characterData: character,
    loadingType: characterLoadingType,
    errorType: characterErrorType,
  } = useLoadCharacter({ characterId: characterId ?? "", withTranslation: true });
  const [stateIsFavoriteLoading, setStateIsFavoriteLoading] = useState(false);
  const [stateIsFavorite, setStateIsFavorite] = useState(false);
  const [stateIsShareModalOpen, setStateIsShareModalOpen] = useState(false);
  const isPartner = getIsPartner();
  const [stateIsZoomedAvatarModalOpen, setStateIsZoomedAvatarModalOpen] = useState(false);
  const isAvatarBlurred = (!isAuthenticated || blurNSFW) && character?.avatar_is_nsfw;
  const isPageLoading = !character || characterLoadingType === CharacterFlow.GET_CHARACTER;
  const { hasFeatureFlag: hasLorebook } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_PUBLIC);
  const { hasFeatureFlag: hasCreatorAnalytics } = useFeatureFlag(FEATURE_FLAGS.CREATOR_ANALYTICS);
  const { permissions } = useAuth();
  const hasAnalyticsAdminAccess = useMemo(() => permissions?.includes(ANALYTICS_VIEW_PERMISSION) ?? false, [permissions]);
  const isCharacterOwner = !!onlyUserId && character?.creator_user_id === onlyUserId;
  // Analytics only carries data for publicly published bots - private,
  // hidden, and unlisted bots either don't have tracked traffic or open
  // an empty dashboard, so don't surface the entry point there.
  const isCharacterPublic = character?.visibility === CHARACTER_VISIBILITY.PUBLIC;
  const canViewAnalytics = hasCreatorAnalytics && isCharacterPublic && (hasAnalyticsAdminAccess || isCharacterOwner);

  // When a private Lorebook is attached to a public chatbot,
  // the attached Lorebook must not be visible on the public chatbot profile to other users.
  const lorebookIndicator = getCharacterLorebookProfileIndicator({ character, isCreator: isCharacterOwner });
  const hasLorebookDisplayed = hasLorebook && lorebookIndicator;
  const showMoreLabel = t("chatbot:profile.showMore");
  const showLessLabel = t("chatbot:profile.showLess");
  const characterRatingScoreLabel = formatCharacterRatingScore(character?.rating_score);

  useEffect(() => {
    if (!!characterId && !likedBots?.includes(characterId)) {
      setStateIsFavorite(false);
    } else {
      setStateIsFavorite(true);
    }
    // TODO(igor-nextday): [NX-7407] pre-existing effect intentionally re-runs only on `likedBots`; behaviour change requires a dedicated PR.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This effect disables exhaustive-deps lint to avoid changing legacy logic
  }, [likedBots]);

  // character endpoint returns empty object when someone tries to access a private bot not owned by them.
  const isCharacterEmpty = character && Object.keys(character).length === 0;
  const hasCharacterLoadError = characterErrorType === CharacterFlow.GET_CHARACTER;

  const metaProperties = useMemo(() => ({ characterName: character?.name ?? undefined }), [character?.name]);

  if (hasCharacterLoadError || isCharacterEmpty) {
    return <NotFoundPage />;
  }

  if (isPageLoading) {
    return (
      <>
        {hasCharacterLoadError ? <PageStaticMetaTags pageId="notFound" prerenderStatusCode={NOT_FOUND_STATUS} /> : null}
        <div className="flex justify-center items-center grow">
          <Spinner size={SpinnerSize.LARGE} />
        </div>
      </>
    );
  }

  // If definition visibility is false then only name, title, creator name & Tags should be visible.
  const isPageNotAccessible = character?.definition_visible !== true && character?.creator_user_id !== onlyUserId;
  const isCreatorFixRequired =
    character?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED &&
    !!onlyUserId &&
    character?.creator_user_id === onlyUserId;

  const handleNavigateToAnalytics = () => {
    if (!characterId) {
      return;
    }
    // Admins inspecting someone else's bot need the creatorId query param so the
    // analytics page loads the correct creator context. Owners hit the same URL
    // without it - the backend defaults to the authenticated user.
    const shouldPassCreatorId = hasAnalyticsAdminAccess && !isCharacterOwner && !!character?.creator_user_id;
    const qs = shouldPassCreatorId ? `?creatorId=${encodeURIComponent(character.creator_user_id)}` : "";
    navigate(`${ROUTES.creatorAnalytics}/${characterId}${qs}`);
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      dispatch(setIsOverlayLogin(true));
      return;
    }
    setStateIsFavoriteLoading(true);
    if (characterId) {
      await onUserLikebot(characterId, screenName as Screen);
      if (stateIsFavorite) {
        await sendUserInteractionAddBookmark(recombeeUserId, characterId);
      }
    }
    setStateIsFavoriteLoading(false);
  };

  return (
    <Row className="w-full flex flex-col max-w-[700px] gap-lg mx-auto">
      <PageStaticMetaTags
        pageId="characterDefinition"
        properties={metaProperties}
        prerenderStatusCode={isCharacterEmpty ? NOT_FOUND_STATUS : undefined}
        ogImage={
          getAvatarUrl(character?.avatar_url || "", {
            preset: AvatarPreset.IMAGE_500X650,
          }) || undefined
        }
        jsonLdTokens={{
          webPageName: character?.name ?? "",
          webPageDescription: character?.title ?? "",
          webPageAuthor: character?.creator_username ?? "",
          webPageMainEntity: character?.name ?? "",
        }}
      />
      <Row>
        <Col>
          <NavigateBackButtonV2 />
        </Col>
        <Col className="max-w-[80%]">
          <CapabilityGate capability={SubCapabilityKey.CharacterTranslate}>
            <CharacterTranslation
              dataTestId="characterTranslationCapabilityGate"
              characterId={characterId}
              style="profile-v2"
              characterLanguage={character?.language}
              characterVisibility={character?.visibility}
            />
          </CapabilityGate>
        </Col>
      </Row>

      <Row className="gap-5 flex-col sm:flex-row">
        <AvatarWithModal
          src={character?.avatar_url || ""}
          isOpen={stateIsZoomedAvatarModalOpen}
          onOpenChange={setStateIsZoomedAvatarModalOpen}
          blurred={isAvatarBlurred}
        >
          <Image
            src={
              getAvatarUrl(character?.avatar_url || "", {
                preset: isMobile ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_640X640,
              }) || undefined
            }
            alt="avatar image"
            className={`cursor-pointer w-full aspect-[3/4] mob:w-[183px] mob:h-[244px] object-cover rounded-md ${isAvatarBlurred ? "blur sm:blur-lg" : "blur-none"}
            `}
            onClick={() => setStateIsZoomedAvatarModalOpen(true)}
          />
        </AvatarWithModal>

        <Row className="flex-col gap-md">
          <Col className="gap-1">
            <Text variant={TextVariants.MOBILE_HEADING_3_BOLD} as="h1" className="my-0">
              {character?.name}
            </Text>
            <LocalizedLink className="text-link" to={`/creator/${character?.creator_username}`} aria-label="creator-profile">
              <Text variant={TextVariants.LABEL_LARGE_REGULAR}> @{character?.creator_username}</Text>
            </LocalizedLink>
          </Col>
          <Row className="flex-wrap gap-3 opacity-60 text-sm">
            <Row className="gap-1 items-center">
              <Icon icon={IconVariants.MESSAGE} size={IconSize.SMALL} />
              <Text>{formatNumber(character?.num_messages || 0) ?? "0"}</Text>
            </Row>

            {characterRatingScoreLabel !== undefined ? (
              <Row className="gap-1 items-center">
                <Icon icon={IconVariants.LIKE} size={IconSize.SMALL} />
                <Text>{characterRatingScoreLabel}</Text>
              </Row>
            ) : null}

            {!isPartner && !!character?.token_count && character.token_count > 0 && (
              <LocalizedLink
                to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId ?? ""]?.docs}/advanced/tokens`}
                target="_blank"
                aria-label="tokens-info"
              >
                <Row className="gap-1 items-center">
                  <Icon icon={IconVariants.INFO} size={IconSize.SMALL} />
                  <Text className="flex items-center gap-1">
                    {formatNumber(character.token_count, true)} <Trans i18nKey="chatbot:profile.tokens" />
                  </Text>
                </Row>
              </LocalizedLink>
            )}
          </Row>
          {hasLorebookDisplayed ? (
            <div>
              {!lorebookIndicator.isInteractionDisabled ? (
                <Button
                  onClick={() => lorebookIndicator.canNavigate && navigate(`/lorebook/${lorebookIndicator.id}`)}
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.SMALL}
                  iconStart={IconVariants.BOOK_TEXT}
                >
                  {lorebookIndicator.name}
                </Button>
              ) : (
                <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.SMALL} iconStart={IconVariants.BOOK_LOCK} disabled>
                  <Trans i18nKey="chatbot:profile.privateLorebook" />
                </Button>
              )}
            </div>
          ) : null}
          <Row className="flex-wrap gap-1">
            {isCreatorFixRequired ? (
              <Button iconStart={IconVariants.MESSAGE_MORE} aria-label="chat-now" disabled>
                <Trans i18nKey="common:chatNow" />
              </Button>
            ) : (
              <LocalizedLink to={`/chat/${characterId}`} aria-label={`chat-with-${character.name}`}>
                <Button iconStart={IconVariants.MESSAGE_MORE} aria-label="chat-now">
                  <Trans i18nKey="common:chatNow" />
                </Button>
              </LocalizedLink>
            )}
            {isCharacterPublic ? (
              <Button
                iconStart={IconVariants.FAVORITE}
                variant={ButtonVariant.TEXT}
                // TODO(igor-nextday): [NX-7407] Refactor pre-existing async favorite handler to fire-and-forget pattern; for now, keeping as is to keep this PR focused.
                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- eslint rule requires sync handlers, but handleFavorite is async and not easily changed due to existing async logic.
                onClick={handleFavorite}
                loading={stateIsFavoriteLoading}
                aria-label={stateIsFavorite ? "favorited" : "favorite"}
                classNames={{
                  icon: stateIsFavorite ? "text-red-9 fill-red-9" : undefined,
                }}
              >
                {stateIsFavorite ? <Trans i18nKey="common:favorited" /> : <Trans i18nKey="common:favorite" />}
              </Button>
            ) : null}
            <CapabilityGate capability={SubCapabilityKey.CharacterShareLink}>
              <Button
                iconStart={IconVariants.SHARE}
                variant={ButtonVariant.TEXT}
                onClick={() => setStateIsShareModalOpen(true)}
                aria-label="share"
                data-testid="ChatbotProfile-ShareButton"
              >
                <Trans i18nKey="common:share" />
              </Button>
            </CapabilityGate>
            {canViewAnalytics ? (
              <Button
                iconStart={IconVariants.CHART_NO_AXES_COMBINED}
                variant={ButtonVariant.TEXT}
                onClick={handleNavigateToAnalytics}
                aria-label="view-analytics"
              >
                {ANALYTICS_BUTTON_LABEL}
              </Button>
            ) : null}
          </Row>
          <Text variant={TextVariants.LABEL_LARGE_REGULAR}>{character?.title}</Text>
          <TagSuggestion id={character.id} tags={character.tags} />
        </Row>
      </Row>
      <TextBox
        label={t("chatbot:profile.greeting")}
        text={character?.greeting ?? ""}
        collapsed={!isDesktop}
        showMoreLabel={showMoreLabel}
        showLessLabel={showLessLabel}
      />
      {!isPageNotAccessible ? (
        <>
          {character?.persona ? (
            <TextBox
              label={t("chatbot:profile.personality")}
              text={character?.persona}
              collapsed={!isDesktop}
              showMoreLabel={showMoreLabel}
              showLessLabel={showLessLabel}
            />
          ) : null}
          {character?.scenario ? (
            <TextBox
              label={t("chatbot:profile.scenario")}
              text={character?.scenario}
              collapsed={!isDesktop}
              showMoreLabel={showMoreLabel}
              showLessLabel={showLessLabel}
            />
          ) : null}
          {character?.dialogue ? (
            <TextBox
              label={t("chatbot:profile.exampleDialogues")}
              text={character.dialogue}
              collapsed={!isDesktop}
              showMoreLabel={showMoreLabel}
              showLessLabel={showLessLabel}
            />
          ) : null}
        </>
      ) : null}
      <CapabilityShareCharacterModal
        visible={stateIsShareModalOpen}
        setShowShare={setStateIsShareModalOpen}
        characterId={characterId}
        characterName={character.name}
      />
    </Row>
  );
}
